import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CheckCircle2, Lock, BookOpen, Loader2, Award } from "lucide-react";
import { toast } from "sonner";

function LessonDialog({ lessonId, open, onClose, onComplete }) {
  const [lesson, setLesson] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!lessonId) return;
    setLesson(null); setAnswers({}); setResult(null);
    api.get(`/doctrine/lesson/${lessonId}`).then((r) => setLesson(r.data)).catch(() => {});
  }, [lessonId]);

  const submit = async () => {
    setBusy(true);
    try {
      const arr = (lesson.quiz_json?.questions || []).map((_, i) => answers[i] ?? -1);
      const r = await api.post(`/doctrine/lesson/${lessonId}/complete`, { answers: arr });
      setResult(r.data);
      if (r.data.reward?.leveled_up) toast.success(`Ranked up to ${r.data.reward.rank}!`);
      else toast.success(`Lesson complete · ${r.data.score}%`);
      onComplete?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not complete"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-[#0F1115] border-white/[0.1] max-h-[88vh] overflow-auto scrollbar-thin">
        {!lesson ? <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
          <>
            <DialogHeader>
              <div className="mono-chip green-text">YEAR {lesson.year} · +{lesson.xp_reward} XP</div>
              <DialogTitle className="font-display text-xl">{lesson.title}</DialogTitle>
            </DialogHeader>
            <p className="text-[var(--bone-muted)] leading-relaxed" style={{ maxWidth: "68ch" }}>{lesson.lesson_text}</p>
            {lesson.examples?.length > 0 && (
              <div className="mt-4 space-y-2">
                {lesson.examples.map((ex, i) => (
                  <div key={i} className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-2.5 text-sm text-[var(--bone-muted)]">{ex}</div>
                ))}
              </div>
            )}
            <div className="mt-4 rounded-lg gold-edge p-3"><span className="mono-chip gold-text">CORE RULE</span><div className="mt-1 text-sm">{lesson.litrametry_rule}</div></div>

            <div className="mt-6">
              <div className="text-sm font-display font-semibold mb-3">Mini Quiz</div>
              {(lesson.quiz_json?.questions || []).map((q, qi) => (
                <div key={qi} className="mb-4">
                  <div className="text-sm mb-2">{qi + 1}. {q.q}</div>
                  <RadioGroup value={String(answers[qi] ?? "")} onValueChange={(v) => setAnswers({ ...answers, [qi]: Number(v) })}>
                    {q.options.map((opt, oi) => {
                      const correct = result && result.answer_key?.[qi] === oi;
                      const wrong = result && answers[qi] === oi && result.answer_key?.[qi] !== oi;
                      return (
                        <label key={oi} className={`flex items-center gap-2 rounded-lg border p-2.5 text-sm cursor-pointer ${correct ? "border-[rgba(20,90,67,0.7)] bg-[rgba(20,90,67,0.12)]" : wrong ? "border-[#C24B4B]/60" : "border-white/[0.08]"}`} data-testid={`quiz-q${qi}-opt${oi}`}>
                          <RadioGroupItem value={String(oi)} disabled={!!result} /> <span>{opt}</span>
                        </label>
                      );
                    })}
                  </RadioGroup>
                </div>
              ))}
              {!result ? (
                <Button onClick={submit} disabled={busy} className="bg-[var(--primary)] hover:opacity-90" data-testid="lesson-quiz-submit-button">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit & complete"}
                </Button>
              ) : (
                <div className="flex items-center gap-3">
                  <Badge className="green-text border-[rgba(20,90,67,0.5)]" variant="outline">Score {result.score}%</Badge>
                  {result.badge_unlock && <Badge className="gold-edge gold-text bg-transparent"><Award className="w-3 h-3 mr-1" />{result.badge_unlock}</Badge>}
                  <Button variant="outline" className="glass ml-auto" onClick={onClose}>Done</Button>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Doctrine() {
  const [data, setData] = useState(null);
  const [letters, setLetters] = useState(null);
  const [openLesson, setOpenLesson] = useState(null);

  const load = async () => {
    const [y, l] = await Promise.all([api.get("/doctrine/lessons"), api.get("/doctrine/letters")]);
    setData(y.data); setLetters(l.data);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-display font-semibold">Doctrine Mode</h1>
        <p className="text-[var(--bone-muted)] text-sm">Elite education for anybody. Year 1 to Year 10.</p>
      </div>

      <Tabs defaultValue="curriculum">
        <TabsList className="bg-white/[0.04]">
          <TabsTrigger value="curriculum" data-testid="doctrine-tab-curriculum">Curriculum</TabsTrigger>
          <TabsTrigger value="hall" data-testid="doctrine-tab-hall">The Letter Hall</TabsTrigger>
        </TabsList>

        <TabsContent value="curriculum" className="mt-4">
          {!data ? <div className="text-[var(--bone-faint)] text-sm">Loading…</div> : (
            <Accordion type="single" collapsible defaultValue="year-1" className="space-y-2" data-testid="doctrine-year-selector">
              {data.years.map((y) => (
                <AccordionItem key={y.year} value={`year-${y.year}`} className="litra-card px-4 border-0">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3 flex-1 pr-3">
                      <span className="mono-chip green-text">Y{y.year}</span>
                      <span className="font-display">{y.title}</span>
                      <span className="ml-auto text-xs text-[var(--bone-faint)]">{y.completed}/{y.total}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid sm:grid-cols-2 gap-2 pb-2">
                      {y.lessons.map((l) => (
                        <button key={l.id} onClick={() => setOpenLesson(l.id)} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.025] border border-white/[0.06] text-left hover:border-[rgba(20,90,67,0.5)]" data-testid={`lesson-${l.id}`}>
                          {l.completed ? <CheckCircle2 className="w-4 h-4 green-text shrink-0" /> : <BookOpen className="w-4 h-4 text-[var(--bone-faint)] shrink-0" />}
                          <div className="flex-1"><div className="text-sm">{l.title}</div></div>
                          {l.completed && <span className="mono-chip text-[var(--bone-faint)]">{l.quiz_score}%</span>}
                        </button>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </TabsContent>

        <TabsContent value="hall" className="mt-4">
          {!letters ? <div className="text-[var(--bone-faint)] text-sm">Loading…</div> : (
            <>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-[var(--bone-muted)]">Unlock letter cards by completing letter lessons.</div>
                <Badge variant="outline" className="green-text border-[rgba(20,90,67,0.5)]">{letters.unlocked_count}/26 unlocked</Badge>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                {letters.cards.map((c) => (
                  <div key={c.letter} className={`litra-card p-4 text-center relative ${c.unlocked ? "litra-card-hover" : "opacity-60"}`} data-testid={`letter-card-${c.letter}`}>
                    {!c.unlocked && <Lock className="w-3.5 h-3.5 absolute top-2 right-2 text-[var(--bone-faint)]" />}
                    <div className={`text-3xl font-display font-semibold ${c.unlocked ? "green-text" : "text-[var(--bone-faint)]"}`}>{c.letter}</div>
                    <div className="mt-1 mono-chip text-[var(--bone-faint)]">{c.technical_name}</div>
                    {c.unlocked && <div className="mt-2 text-[11px] text-[var(--bone-muted)] leading-snug">{c.keywords?.slice(0, 3).join(" · ")}</div>}
                  </div>
                ))}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      <LessonDialog lessonId={openLesson} open={!!openLesson} onClose={() => setOpenLesson(null)} onComplete={load} />
    </div>
  );
}
